import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPLOYEE_STATUS_MAP,
  PAYROLL_RUN_STATUS_MAP,
  salesTargetLabel,
  type EmployeeStatus,
  type PayrollRunStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date, money, num } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, ErrorState, Modal, Notice, PageHead } from '../../components/ui';
import { DocPreview } from '../../components/DocPreview';
import {
  AchieveRing,
  Fact,
  FactGrid,
  MoneyTile,
  YearSwitch,
} from '../../components/staff';
import { useToast } from '../../components/Toast';

/* ==========================================================================
   ملفُّ مندوب المبيعات — هدفُه وما حقّقه وما استحقّه
   ========================================================================== */

/**
 * صفحةٌ لكلّ مندوب.
 *
 * كان ضبطُه يُفتح تحت صفّه في القائمة، فتطول القائمةُ بما لا يخصّ قارئَها
 * ويضيع ما بين الصفوف. والمندوبُ سجلٌّ له تاريخٌ وأرقام — فله صفحةٌ يُفتح
 * فيها ويُشار إليها برابط.
 *
 * وما يُحقَّق يُقرأ من الفواتير الصادرة لا من الصفقات: الصفقةُ وعدٌ
 * والفاتورةُ مطالبة — والهدفُ يُقاس بما خرج للعميل.
 *
 * وفي الصفحة ثلاثةُ أسئلةٍ عن رجلٍ واحد: من هو، وإلى أين وصل، وماذا
 * استحقّ — والقراءةُ أوّلًا. أمّا الضبطُ فليس سؤالًا يُقرأ: هو عملٌ
 * يُقصَد قصدًا، فانتقل إلى نافذةٍ تُفتح بأيقونةٍ ولا تشغل ثلث الصفحة على
 * من جاء يقرأ.
 */

interface Period {
  key: string;
  target: number | null;
  achieved: number;
  pct: number | null;
}

interface CommissionLine {
  id: string;
  runId: string;
  period: string;
  periodLabel: string;
  status: PayrollRunStatus;
  basisLabel: string;
  basisAmount: number;
  target: number | null;
  rate: number;
  eligible: number;
  commission: number;
  documentCount: number;
  paid: boolean;
}

interface RepData {
  year: number;
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
    email: string | null;
    phone: string | null;
    hireDate: string;
    endDate: string | null;
    status: string;
    isSalesRep: boolean;
    managerId: string | null;
    managerName: string | null;
    userId: string | null;
  };
  commissionYear: {
    earned: number;
    paid: number;
    draft: number;
    lines: CommissionLine[];
  };
  policy: {
    commissionRate: number | null;
    discountBudget: number | null;
    discountBudgetOwn: number | null;
    usingDefaultBudget: boolean;
  };
  year_: { target: number | null; achieved: number; pct: number | null };
  quarters: Array<Period & { q: number }>;
  months: Array<Period & { month: number }>;
  missing: string[];
}

/**
 * قسمةُ مبلغٍ على أجزاءٍ متساوية بلا ضياع.
 *
 * القسمةُ الساذجة تكسر: مليونٌ على ثلاثةٍ يعطي ٣٣٣٬٣٣٣ ثلاثَ مرّات
 * فيضيع واحد. فيُجبَر الكسرُ في آخر جزءٍ ليطابق المجموعُ الأصلَ تمامًا —
 * وإلّا قال النظامُ إنّ مجموع الشهور يقلّ عن السنة وهو لم يقلّ.
 */
function splitEvenly(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  const base = Math.floor(total / parts);
  const out = new Array(parts).fill(base);
  out[parts - 1] = total - base * (parts - 1);
  return out;
}

const asNum = (v: string) => Number(v) || 0;
const asText = (n: number) => (n > 0 ? String(n) : '');

export function SalesRep() {
  const { id = '' } = useParams();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const toast = useToast();
  const queryClient = useQueryClient();

  /* القسيمةُ المفتوحة، ونافذةُ الضبط — طبقتان لا تُفتحان معًا. */
  const [slip, setSlip] = useState<CommissionLine | null>(null);
  const [setup, setSetup] = useState(false);

  const q = useQuery({
    queryKey: ['sales', 'rep', id, year],
    queryFn: () => api<RepData>(`/sales/targets/team/${id}${qs({ year })}`),
    placeholderData: (prev) => prev,
  });

  const [yearTarget, setYearTarget] = useState('');
  const [quarters, setQuarters] = useState<string[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [rate, setRate] = useState('');
  const [budget, setBudget] = useState('');
  const [dirty, setDirty] = useState(false);

  const d = q.data;
  useEffect(() => {
    if (!d || dirty) return;
    setYearTarget(d.year_.target ? String(d.year_.target) : '');
    setQuarters(d.quarters.map((x) => (x.target ? String(x.target) : '')));
    setMonths(d.months.map((x) => (x.target ? String(x.target) : '')));
    setRate(d.policy.commissionRate ? String(d.policy.commissionRate) : '');
    setBudget(
      d.policy.discountBudgetOwn ? String(d.policy.discountBudgetOwn) : '',
    );
  }, [d, dirty]);

  const save = useMutation({
    mutationFn: async () => {
      const entries = [
        { kind: 'YEAR' as const, key: String(year), amount: Number(yearTarget) || 0 },
        ...quarters.map((v, i) => ({
          kind: 'QUARTER' as const,
          key: `${year}-Q${i + 1}`,
          amount: Number(v) || 0,
        })),
        ...months.map((v, i) => ({
          kind: 'MONTH' as const,
          key: `${year}-${String(i + 1).padStart(2, '0')}`,
          amount: Number(v) || 0,
        })),
      ];
      await api(`/sales/targets/team/${id}`, { method: 'POST', body: { entries } });
      await api(`/sales/targets/${id}`, {
        method: 'PATCH',
        body: {
          commissionRate: rate.trim() === '' ? null : Number(rate),
          discountBudget: budget.trim() === '' ? null : Number(budget),
        },
      });
    },
    onSuccess: () => {
      toast('حُفظت إعدادات المندوب');
      setDirty(false);
      setSetup(false);
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  if (q.isError) {
    return (
      <div className="page">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }

  /*
   * الأهدافُ مترابطة: ينزل الرقمُ ويصعد المجموع.
   *
   * كان الأمرُ يدويًّا: تكتب السنةَ ثمّ تضغط «وزّع» ثمّ تصحّح اثني عشر
   * حقلًا، وإن عدّلت شهرًا بقيت السنةُ على رقمها القديم فيقرأ الجدولُ
   * هدفًا لا أحدَ يقصده. فصار الربطُ حيًّا:
   *
   *   - السنةُ تنزل: تُقسَم أرباعًا، وكلُّ ربعٍ يُقسَم شهورَه.
   *   - والربعُ ينزل إلى شهوره، ويصعد فتصير السنةُ مجموعَ الأرباع.
   *   - والشهرُ يصعد: ربعُه مجموعُ شهوره، وسنتُه مجموعُ أرباعها.
   *
   * فالمجموعُ هو الحقيقة دائمًا، ولا يبقى رقمٌ يناقض ما تحته.
   */
  const applyYear = (v: string) => {
    setYearTarget(v);
    const total = asNum(v);
    const qs4 = splitEvenly(total, 4);
    setQuarters(qs4.map(asText));
    setMonths(qs4.flatMap((x) => splitEvenly(x, 3)).map(asText));
    setDirty(true);
  };

  const applyQuarter = (qi: number, v: string) => {
    const nextQ = quarters.map((x, i) => (i === qi ? v : x));
    setQuarters(nextQ);
    /* الربعُ ينزل إلى شهوره الثلاثة. */
    const parts = splitEvenly(asNum(v), 3).map(asText);
    setMonths((prev) =>
      prev.map((x, i) => (Math.floor(i / 3) === qi ? parts[i % 3] : x)),
    );
    setYearTarget(asText(nextQ.reduce((n, x) => n + asNum(x), 0)));
    setDirty(true);
  };

  const applyMonth = (mi: number, v: string) => {
    const nextM = months.map((x, i) => (i === mi ? v : x));
    setMonths(nextM);
    /* الشهرُ يصعد: ربعُه مجموعُ شهوره، والسنةُ مجموعُ الأرباع. */
    const nextQ = [0, 1, 2, 3].map((x) =>
      asText(nextM.slice(x * 3, x * 3 + 3).reduce((n, y) => n + asNum(y), 0)),
    );
    setQuarters(nextQ);
    setYearTarget(asText(nextQ.reduce((n, x) => n + asNum(x), 0)));
    setDirty(true);
  };

  /* إعادةُ التوزيع بالتساوي — لمن عدّل يدويًّا ثمّ أراد البدءَ من جديد. */
  const spread = () => applyYear(yearTarget);

  const cy = d?.commissionYear;

  return (
    <div className="page">
      <PageHead
        title={d?.employee.name ?? 'مندوب'}
        subtitle={
          d
            ? `${d.employee.jobTitle ?? 'مندوب مبيعات'}${d.employee.code ? ` · ${d.employee.code}` : ''} · التحق في ${date(d.employee.hireDate)}`
            : undefined
        }
        actions={
          <>
            <YearSwitch
              year={year}
              onYear={(y) => {
                setYear(y);
                setDirty(false);
              }}
              max={now.getFullYear() + 1}
            />
            <button
              className="btn btn-icon"
              onClick={() => setSetup(true)}
              title="ضبط الأهداف والسياسة"
              aria-label="ضبط الأهداف والسياسة"
            >
              <Icon name="settings" size={15} />
            </button>
            <Link to="/sales/team" className="btn">
              <Icon name="arrowLeft" size={15} />
              الفريق
            </Link>
          </>
        }
      />

      {d && d.missing.length > 0 && (
        <div className="notices">
          <Notice
            kind="warning"
            icon="alert"
            title={`ينقصه ${d.missing.join(' و')}`}
            action={
              <button className="btn btn-sm" onClick={() => setSetup(true)}>
                اضبطه الآن
              </button>
            }
          >
            لا يدخل الكشوف ولا يُقاس أداؤه حتى يُضبط.
          </Notice>
        </div>
      )}

      {/*
        من هو أوّلًا، ثمّ إلى أين وصل.

        والبياناتُ تُقرأ هنا ولا تُحرَّر: مصدرُها ملفُّ الموارد البشرية،
        وتحريرُها في شاشتين يعني اسمًا في إحداهما لا يعرفه الآخر. فالزرُّ
        يذهب إلى الأصل.
      */}
      <div className="two-col mb-4" style={{ alignItems: 'stretch' }}>
        <ProfileCard d={d} />
        <AchieveCard d={d} year={year} />
      </div>

      {/*
        وعمولاتُه في السنة نفسها.

        الكشوفُ تُنشأ شهرًا شهرًا في شاشة العمولات، وهنا يُقرأ نصيبُه منها:
        ما استحقّه وما صُرف وما لم يُعتمد بعد. والمسوّدةُ تُفصَل عن المستحقّ
        ولا تُجمع معه — رقمٌ لم يُعتمد قد يُعاد حسابُه أو يُحذف كشفُه، ووعدٌ
        برقمٍ لا يأتي أسوأ من لا رقم.
      */}
      <div className="card mb-4">
        <div className="card-head">
          <h2 className="card-title">عمولاته في {year}</h2>
          <span className="tiny mute">
            ثلاثةُ شهورٍ في النظرة، والباقي بالتمرير
          </span>
        </div>

        <div className="card-body">
          <div className="cz">
            <MoneyTile
              tone="var(--brand)"
              icon="wallet"
              label="المستحقّ"
              value={money(cy?.earned ?? 0)}
              share={
                cy && cy.earned > 0 ? Math.round((cy.paid / cy.earned) * 100) : 0
              }
              note="من كشوفٍ معتمدةٍ أو مدفوعة"
            />
            <MoneyTile
              tone="var(--success)"
              icon="check"
              label="المصروف"
              value={money(cy?.paid ?? 0)}
              share={
                cy && cy.earned > 0 ? Math.round((cy.paid / cy.earned) * 100) : 0
              }
              note={
                cy && cy.earned > cy.paid
                  ? `بقي ${money(cy.earned - cy.paid)} لم يُصرف`
                  : 'نزل بندًا في مرتّبه'
              }
            />
            <MoneyTile
              tone="var(--warning)"
              icon="clock"
              label="تحت المراجعة"
              value={money(cy?.draft ?? 0)}
              share={
                cy && cy.draft + cy.earned > 0
                  ? Math.round((cy.draft / (cy.draft + cy.earned)) * 100)
                  : 0
              }
              note="كشوفٌ لم تُعتمد — قد تتغيّر"
            />
            <MoneyTile
              tone="var(--mv-3)"
              icon="target"
              label="نسبته"
              value={
                d?.policy.commissionRate
                  ? `${num(d.policy.commissionRate)}٪`
                  : '—'
              }
              note={
                d?.policy.commissionRate
                  ? 'تُطبَّق على الخاضع للعمولة'
                  : 'لم تُضبط بعد — لا يدخل كشفًا'
              }
            />
          </div>
        </div>

        {(cy?.lines.length ?? 0) > 0 ? (
          <div
            className="table-wrap sticky-head rows-cap"
            /* صفوفُ العمولات أطولُ: فيها شارةٌ وزرّ. */
            style={{ ['--rows' as string]: 3, ['--rowh' as string]: '68px' }}
          >
            <table className="table">
              <thead>
                <tr>
                  <th>الشهر</th>
                  <th>الأساس</th>
                  <th className="col-num">ما احتُسب عليه</th>
                  <th className="col-num">الخاضع</th>
                  <th className="col-num">النسبة</th>
                  <th className="col-num">العمولة</th>
                  <th className="col-num">المستندات</th>
                  <th>الكشف</th>
                  <th className="col-act" />
                </tr>
              </thead>
              <tbody>
                {(cy?.lines ?? []).map((l) => (
                  <tr key={l.id}>
                    <td className="cell-strong">{l.periodLabel}</td>
                    <td className="cell-mute">{l.basisLabel}</td>
                    <td className="num cell-mute">{money(l.basisAmount)}</td>
                    <td className="num">{money(l.eligible)}</td>
                    <td className="num cell-mute">{num(l.rate)}٪</td>
                    <td className="num cell-strong">{money(l.commission)}</td>
                    <td className="num cell-mute">{num(l.documentCount)}</td>
                    <td>
                      <Badge kind={PAYROLL_RUN_STATUS_MAP[l.status].kind}>
                        {PAYROLL_RUN_STATUS_MAP[l.status].label}
                      </Badge>
                    </td>
                    <td className="col-act">
                      <button
                        className="btn btn-sm"
                        onClick={() => setSlip(l)}
                        title="قسيمةُ الشهر — بهويّة الشركة، تُطبع أو تُحفظ"
                      >
                        <Icon name="eye" size={13} />
                        معاينة
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="tiny mute center" style={{ padding: 'var(--sp-4)' }}>
            {d && !d.policy.commissionRate
              ? 'لا نسبة عمولةٍ مضبوطة — لا يدخل كشفًا حتى تُضبط.'
              : `لا عمولات في ${year} — الكشوف تُنشأ شهرًا شهرًا من شاشة العمولات.`}
          </p>
        )}
      </div>

      <QuartersCard d={d} year={year} />
      <MonthsCard d={d} year={year} />

      {setup && (
        <SetupModal
          year={year}
          yearTarget={yearTarget}
          quarters={quarters}
          months={months}
          rate={rate}
          budget={budget}
          usingDefaultBudget={d?.policy.usingDefaultBudget ?? true}
          defaultBudget={d?.policy.discountBudget ?? null}
          saving={save.isPending}
          onYear={applyYear}
          onQuarter={applyQuarter}
          onMonth={applyMonth}
          onRate={(v) => {
            setRate(v);
            setDirty(true);
          }}
          onBudget={(v) => {
            setBudget(v);
            setDirty(true);
          }}
          onSpread={spread}
          onSave={() => save.mutate()}
          onClose={() => setSetup(false)}
        />
      )}

      {/*
        والقسيمةُ مستندٌ لا نافذة.

        المندوبُ يأخذها ورقةً ويحتفظ بها، فتخرج بترويسة الشركة وشعارها كما
        تخرج الفاتورة — ومعاينتُها هي صفحةُ الطباعة نفسها في إطارٍ داخليّ:
        ما يُرى هو ما سيُطبع لا تقريبٌ له.
      */}
      {slip && d && (
        <DocPreview
          path={`/print/commission-slip/${slip.runId}/${d.employee.id}`}
          title={`قسيمة عمولة — ${slip.periodLabel}`}
          onClose={() => setSlip(null)}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   الملفُّ الوظيفي
   ========================================================================== */

/**
 * الملفُّ الوظيفيُّ كما هو في الموارد البشرية.
 *
 * ويُقال فيه ما لا يقوله جدولُ أهداف: من لا حسابَ له على النظام لا فاتورةَ
 * تُنسب إليه، فيُقرأ إنجازُه صفرًا وهو قد باع. فالحقيقةُ تُكتب في خانتها لا
 * تُترك للقارئ يستنتجها من رقمٍ فارغ.
 */
function ProfileCard({ d }: { d?: RepData }) {
  const st = d ? EMPLOYEE_STATUS_MAP[d.employee.status as EmployeeStatus] : null;
  return (
    <div className="card col-card">
      <div className="card-head">
        <h2 className="card-title">الملفّ الوظيفي</h2>
        {d && (
          <Link
            to={`/hr/employees/${d.employee.id}`}
            className="btn btn-sm"
            style={{ marginInlineStart: 'auto' }}
            title="البياناتُ تُحرَّر في الموارد البشرية لا هنا"
          >
            <Icon name="user" size={13} />
            ملفُّه في الموارد البشرية
          </Link>
        )}
      </div>
      <div className="card-body col-body">
        {!d ? (
          <div className="skeleton" style={{ height: 140 }} />
        ) : (
          <FactGrid>
            <Fact
              icon="briefcase"
              label="المسمّى الوظيفي"
              value={d.employee.jobTitle || '—'}
            />
            <Fact
              icon="building"
              label="القسم"
              value={d.employee.department || '—'}
            />
            <Fact icon="clipboard" label="الكود" value={d.employee.code || '—'} ltr />
            <Fact
              icon="check"
              label="الحالة"
              value={st ? <Badge kind={st.kind}>{st.label}</Badge> : '—'}
            />
            <Fact
              icon="users"
              label="المدير المباشر"
              value={
                d.employee.managerId && d.employee.managerName ? (
                  <Link
                    to={`/hr/employees/${d.employee.managerId}`}
                    className="link"
                  >
                    {d.employee.managerName}
                  </Link>
                ) : (
                  '—'
                )
              }
            />
            <Fact
              icon="calendar"
              label="تاريخ الالتحاق"
              value={date(d.employee.hireDate)}
            />
            {d.employee.endDate && (
              <Fact
                icon="clock"
                label="انتهاء الخدمة"
                value={date(d.employee.endDate)}
              />
            )}
            <Fact
              icon="mail"
              label="البريد"
              value={d.employee.email || '—'}
              ltr
              wide
            />
            <Fact icon="phone" label="الهاتف" value={d.employee.phone || '—'} ltr />
            <Fact
              icon="user"
              label="حساب النظام"
              value={
                d.employee.userId ? (
                  'مرتبط'
                ) : (
                  <span className="tiny" style={{ color: 'var(--warning)' }}>
                    لا حساب — لا تُنسب إليه فاتورةٌ فيُقرأ إنجازُه صفرًا.
                  </span>
                )
              }
            />
          </FactGrid>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   الإنجاز
   ========================================================================== */

/**
 * قوسُ الإنجاز — كم قُطع من الهدف، ولونُه يقول الحال.
 *
 * الشريطُ يمتدّ بلا نهايةٍ مرئيّة فلا يُعرف منه كم بقي؛ والدائرةُ لها حدٌّ
 * فيُقرأ المقطوعُ والمتبقّي معًا. واللونُ يمشي مع القوس من الأحمر إلى
 * الأخضر: يُعرف الحالُ قبل أن تُقرأ النسبة.
 */
function AchieveCard({ d, year }: { d?: RepData; year: number }) {
  const pct = d?.year_.pct ?? 0;
  return (
    <div className="card col-card">
      <div className="card-head">
        <h2 className="card-title">إنجاز {year}</h2>
        <span className="tiny mute">المحقَّق من الفواتير الصادرة</span>
      </div>
      <div className="card-body col-body">
        {!d ? (
          <div className="skeleton" style={{ height: 148 }} />
        ) : d.year_.target ? (
          <div className="ach">
            <AchieveRing pct={pct} />
            <div className="ach-side">
              <div className="ach-row">
                <span className="pf-k">الهدف السنوي</span>
                <b className="num ach-val is-mute">{money(d.year_.target)}</b>
              </div>
              <div className="ach-row">
                <span className="pf-k">المحقَّق</span>
                <b className="num ach-val">{money(d.year_.achieved)}</b>
              </div>
              <div className="ach-row">
                <span className="pf-k">المتبقّي</span>
                <b className="num ach-val is-mute">
                  {money(Math.max(0, d.year_.target - d.year_.achieved))}
                </b>
              </div>
            </div>
          </div>
        ) : (
          <p className="tiny mute">
            لا هدف سنويّ لهذه السنة — اضبطه من أيقونة الضبط أعلى الصفحة ليُقاس
            عليه.
          </p>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   الأرباع والشهور
   ========================================================================== */

/**
 * أرباعُ السنة — أربعةُ أقواسٍ بأسمائها.
 *
 * أربعةُ صفوفٍ في جدولٍ تُقرأ رقمًا رقمًا، وأربعةُ أقواسٍ تُقرأ دفعةً
 * واحدة: أيُّ ربعٍ أحمرُ وأيُّها أخضر. والاسمُ يُكتب Q1 لا «الربع الأوّل
 * ٢٠٢٦» — أقصرُ فيتّسع له رأسُ البطاقة، وأعرفُ عند من يقرأ الأرقام.
 */
function QuartersCard({ d, year }: { d?: RepData; year: number }) {
  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">أرباع {year}</h2>
        <span className="tiny mute">الهدفُ والمحقَّق في كلّ ربع</span>
      </div>
      <div className="card-body">
        {!d ? (
          <div className="skeleton" style={{ height: 150 }} />
        ) : (
          <div className="qz">
            {d.quarters.map((x) => {
              const pct = x.pct ?? 0;
              return (
                <div
                  className="qz-cell"
                  key={x.key}
                  title={salesTargetLabel('QUARTER', x.key)}
                >
                  <span className="qz-tag">Q{x.q}</span>
                  <AchieveRing
                    pct={pct}
                    small
                    empty={x.target ? undefined : 'بلا هدف'}
                  />
                  <span className="qz-nums">
                    <b className="num qz-done">{money(x.achieved)}</b>
                    <span className="num qz-target">
                      {x.target ? `من ${money(x.target)}` : 'لم يُضبط'}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * شهورُ السنة — جدولٌ بستّةٍ في النظرة وباقيه بالتمرير.
 *
 * اثنا عشر صفًّا تدفع ما بعدها خارج الشاشة، فيُعرض نصفُها ويُطوى نصفُها
 * خلف تمريرٍ بلا علامة: الصفُّ المقطوعُ عند الحافّة يدعو إلى التمرير
 * أبلغَ من شريطٍ رماديّ.
 */
function MonthsCard({ d, year }: { d?: RepData; year: number }) {
  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">شهور {year}</h2>
        <span className="tiny mute">ستّةُ شهورٍ في النظرة، والباقي بالتمرير</span>
      </div>
      <div
        className="table-wrap sticky-head rows-cap"
        style={{ ['--rows' as string]: 6 }}
      >
        <table className="table">
          <thead>
            <tr>
              <th>الشهر</th>
              <th className="col-num">الهدف</th>
              <th className="col-num">المحقَّق</th>
              <th className="col-num">المتبقّي</th>
              <th style={{ width: '38%' }}>الإنجاز</th>
            </tr>
          </thead>
          <tbody>
            {(d?.months ?? []).map((x) => (
              <tr key={x.key}>
                <td className="cell-strong">
                  {salesTargetLabel('MONTH', x.key).replace(` ${year}`, '')}
                </td>
                <td className="num cell-mute">
                  {x.target ? money(x.target) : '—'}
                </td>
                <td className="num">{money(x.achieved)}</td>
                <td className="num cell-mute">
                  {x.target ? money(Math.max(0, x.target - x.achieved)) : '—'}
                </td>
                <td>
                  {x.pct == null ? (
                    <span className="tiny mute">بلا هدف</span>
                  ) : (
                    <span className="row" style={{ gap: 8 }}>
                      <span
                        className="bar-track"
                        style={{ flex: 1, minWidth: 80 }}
                      >
                        <span
                          className="tg-fill"
                          style={{
                            width: `${Math.min(100, x.pct)}%`,
                            ['--tg-pct' as string]: Math.max(
                              1,
                              Math.min(100, x.pct),
                            ),
                          }}
                        >
                          <span className="tg-grad" />
                        </span>
                      </span>
                      <b
                        className={`num tiny${x.pct >= 100 ? ' ach-pct is-hit' : ''}`}
                      >
                        {x.pct}٪
                      </b>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ==========================================================================
   نافذةُ الضبط
   ========================================================================== */

/**
 * الضبطُ في نافذةٍ لا في ثلث الصفحة.
 *
 * من يفتح ملفَّ مندوبٍ يسأل «إلى أين وصل» في كلّ مرّة، ويضبط هدفَه مرّةً
 * في السنة. فالقراءةُ تملأ الصفحة، والعملُ يُفتح بأيقونةٍ حين يُقصَد.
 */
function SetupModal({
  year,
  yearTarget,
  quarters,
  months,
  rate,
  budget,
  usingDefaultBudget,
  defaultBudget,
  saving,
  onYear,
  onQuarter,
  onMonth,
  onRate,
  onBudget,
  onSpread,
  onSave,
  onClose,
}: {
  year: number;
  yearTarget: string;
  quarters: string[];
  months: string[];
  rate: string;
  budget: string;
  usingDefaultBudget: boolean;
  defaultBudget: number | null;
  saving: boolean;
  onYear: (v: string) => void;
  onQuarter: (i: number, v: string) => void;
  onMonth: (i: number, v: string) => void;
  onRate: (v: string) => void;
  onBudget: (v: string) => void;
  onSpread: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title={`ضبط الأهداف والسياسة — ${year}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button
            className="btn"
            onClick={onSpread}
            disabled={(Number(yearTarget) || 0) <= 0}
            title="يوزّع الهدف السنوي بالتساوي على الأرباع والشهور — ثمّ عدّله"
          >
            <Icon name="layers" size={14} />
            وزّع بالتساوي
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            <Icon name="check" size={14} />
            {saving ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid mb-4">
        <label className="field">
          <span className="label">الهدف السنوي ({year})</span>
          <span className="tiny mute" style={{ order: 3 }}>
            يُوزَّع تلقائيًّا على الأرباع والشهور — وتعديلُ أيٍّ منها يُعيد
            حساب ما فوقه.
          </span>
          <input
            className="input num"
            type="number"
            min={0}
            step={1000}
            value={yearTarget}
            onChange={(e) => onYear(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">نسبة العمولة ٪</span>
          <input
            className="input num"
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={rate}
            onChange={(e) => onRate(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">رصيد الخصم الشهري</span>
          <input
            className="input num"
            type="number"
            min={0}
            step={100}
            value={budget}
            onChange={(e) => onBudget(e.target.value)}
          />
          <span className="tiny mute">
            {usingDefaultBudget
              ? `فارغًا يأخذ رصيد الشركة${defaultBudget ? ` (${money(defaultBudget)})` : ''}.`
              : 'رصيدٌ خاصٌّ به.'}
          </span>
        </label>
      </div>

      <TargetGrid
        title="أهداف الأرباع"
        items={quarters.map((v, i) => ({
          key: `${year}-Q${i + 1}`,
          label: `Q${i + 1}`,
          value: v,
        }))}
        sum={quarters.reduce((n, v) => n + (Number(v) || 0), 0)}
        total={Number(yearTarget) || 0}
        onChange={onQuarter}
      />

      <TargetGrid
        title="أهداف الشهور"
        items={months.map((v, i) => ({
          key: `${year}-${i + 1}`,
          label: salesTargetLabel(
            'MONTH',
            `${year}-${String(i + 1).padStart(2, '0')}`,
          ).replace(` ${year}`, ''),
          value: v,
        }))}
        sum={months.reduce((n, v) => n + (Number(v) || 0), 0)}
        total={Number(yearTarget) || 0}
        onChange={onMonth}
      />
    </Modal>
  );
}

/**
 * شبكةُ أهدافٍ بمجموعها.
 *
 * والمجموعُ يُقارَن بالسنويّ ولا يُفرض عليه: قد تقصد ألّا تجمع الشهورُ
 * السنةَ — موسمٌ يُترك بلا هدف. فيُقال الفارقُ ولا يُمنع الحفظ.
 */
function TargetGrid({
  title,
  items,
  sum,
  total,
  onChange,
}: {
  title: string;
  items: Array<{ key: string; label: string; value: string }>;
  sum: number;
  total: number;
  onChange: (index: number, value: string) => void;
}) {
  const diff = total > 0 ? sum - total : 0;
  return (
    <div className="mb-4">
      <div className="row mb-2" style={{ gap: 'var(--sp-2)' }}>
        <h3 className="label" style={{ margin: 0 }}>
          {title}
        </h3>
        <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
          المجموع <span className="num">{money(sum)}</span>
          {total > 0 && diff !== 0 && (
            <>
              {' '}
              ·{' '}
              <span style={{ color: diff > 0 ? 'var(--warning)' : 'var(--ink-4)' }}>
                {diff > 0 ? 'يزيد عن' : 'يقلّ عن'} السنوي بـ{' '}
                <span className="num">{money(Math.abs(diff))}</span>
              </span>
            </>
          )}
        </span>
      </div>
      <div className="tg-grid">
        {items.map((it, i) => (
          <label className="tg-cell" key={it.key}>
            <span className="tiny mute">{it.label}</span>
            <input
              className="input num"
              type="number"
              min={0}
              step={1000}
              value={it.value}
              onChange={(e) => onChange(i, e.target.value)}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
